import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { ApplicationException } from 'common/exceptions/application/ApplicationException';
import { ItemIsNotSavedException } from 'common/exceptions/application/ItemIsNotSavedException';
import { OutlookAttachmentsNotAvailableException } from 'common/exceptions/outlook/OutlookAttachmentsNotAvailableException';
import { OutlookException } from 'common/exceptions/outlook/OutlookException';
import { OutlookItemNotSupported } from 'common/exceptions/outlook/OutlookItemNotSupported';
import { OutlookWrongItemSelectedException } from 'common/exceptions/outlook/OutlookWrongItemSelectedException';
import { AppointmentUids } from 'common/models/AppointmentUids';
import { MessageReferenceIds } from 'common/models/MessageReferenceIds';
import CONFIG from 'config';
import { Attachment } from '../models/Attachment';
import { SupportedAttachmentsResult } from '../models/SupportedAttachmentsResult';
import { SupportedOfficeItem } from './SupportedOfficeItem';
import { findDuplicateMeeting } from './api';
import { assertHasGraphApi } from './assertHasGraphApi';
import { toIsoWithTimeZone } from './dateUtils';
import * as GraphApi from './graphApi';
import * as OutlookAsyncUtils from './outlookAsyncUtils';
import * as ProductAnalytics from './product-analytics';

const TAG = 'OIU3: ';
const NO_SUBJECT = '(No subject)';

export function getUserEmail(
  sharedProperties: Office.SharedProperties | undefined
) {
  return sharedProperties
    ? sharedProperties.owner
    : Office.context.mailbox.userProfile.emailAddress;
}

export function isSameEmail(a: string | undefined, b: string | undefined) {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}

let lastSentItemsCheck: { itemId: string; result: Promise<boolean> };

/**
 * Whether the message is in the user's Sent Items folder.
 *
 * Users can send from an alias, in which case the From address doesn't match {@link getUserEmail}.
 * Reading the user's aliases would need the Graph `User.Read` permission, so the folder is checked instead.
 *
 * Only supported for the user's own mailbox when Graph is enabled, otherwise returns `false`.
 */
async function isInSentItems(
  item: Office.MessageRead,
  sharedProperties: Office.SharedProperties | undefined
): Promise<boolean> {
  if (!CONFIG.isGraphApiEnabled || sharedProperties || !item.itemId) {
    return false;
  }

  // Stakeholders and direction are checked several times for the same item, so reuse the last result
  if (lastSentItemsCheck?.itemId !== item.itemId) {
    lastSentItemsCheck = {
      itemId: item.itemId,
      result: Promise.all([
        GraphApi.getMessageParentFolderId(item.itemId),
        GraphApi.getSentItemsFolderId(),
      ]).then(
        ([parentFolderId, sentItemsFolderId]) =>
          parentFolderId === sentItemsFolderId,
        (error) => {
          console.error(TAG, 'Failed to check if item is in Sent Items', error);
          return false;
        }
      ),
    };
  }

  return await lastSentItemsCheck.result;
}

/**
 * Whether the message was sent by the user, including from one of their aliases.
 */
export async function isMessageSentByUser(
  message: Office.MessageRead,
  sharedProperties: Office.SharedProperties | undefined
): Promise<boolean> {
  return (
    isSameEmail(message.from?.emailAddress, getUserEmail(sharedProperties)) ||
    (await isInSentItems(message, sharedProperties))
  );
}

export function getAsyncLocation(location: Office.Location) {
  const promise = new Promise<string>(function (resolve, reject) {
    location.getAsync((recipientsResult) => {
      if (recipientsResult.status === Office.AsyncResultStatus.Succeeded)
        resolve(recipientsResult.value);
      else reject(recipientsResult.error);
    });
  });
  return promise;
}

export async function getDirectionFromOutlookItem(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
) {
  const userEmail = getUserEmail(sharedProperties);

  let senderEmail = '';

  if (item.itemType === 'message') {
    const message = item as Office.MessageRead;
    return (await isMessageSentByUser(message, sharedProperties))
      ? 'OUT'
      : 'IN';
  } else if (item.itemType === 'appointment') {
    if (isAppointmentReadMode(item)) {
      const appointmentRead = item as Office.AppointmentRead;
      senderEmail = appointmentRead.organizer.emailAddress;
    } else {
      senderEmail = userEmail;
    }
  } else {
    throw new OutlookItemNotSupported();
  }

  return senderEmail === userEmail ? 'OUT' : 'IN';
}

export async function getLocationFromOutlookItem(
  item: SupportedOfficeItem
): Promise<string | null> {
  if (item.itemType === 'message') {
    return null;
  } else if (item.itemType === 'appointment') {
    if (isAppointmentReadMode(item)) {
      const appointment = item as Office.AppointmentRead;
      return appointment.location;
    } else {
      const appointment = item as Office.AppointmentCompose;
      return await getAsyncLocation(appointment.location);
    }
  } else {
    throw new OutlookItemNotSupported();
  }
}

export async function getSubjectFromOutlookItem(
  item: Office.MessageRead & Office.AppointmentCompose & Office.AppointmentRead
) {
  if (item.itemType === 'message') {
    const message = item as Office.MessageRead;
    return message.subject || NO_SUBJECT;
  } else if (item.itemType === 'appointment') {
    if (isAppointmentReadMode(item)) {
      const appointment = item as Office.AppointmentRead;
      return appointment.subject || NO_SUBJECT;
    } else {
      const appointment = item as Office.AppointmentCompose;
      return (
        (await OutlookAsyncUtils.getAsyncSubject(appointment.subject)) ||
        NO_SUBJECT
      );
    }
  } else {
    throw new OutlookItemNotSupported();
  }
}

/**
 * Outlook allows emails without a subject, in which case Graph may return null,
 * so autosave falls back to `(No subject)`.
 */
export function getSubjectFromGraphMessage(message: MicrosoftGraph.Message) {
  return message.subject || NO_SUBJECT;
}

export function getInteractionMethodFromOutlookItem(
  item: Office.MessageRead & Office.AppointmentCompose & Office.AppointmentRead
) {
  if (item.itemType === 'message') return 'Email';
  else if (item.itemType === 'appointment') return 'Meeting';
  else throw new OutlookItemNotSupported();
}

export async function getDateFromOutlookItem(
  item: Office.MessageRead & Office.AppointmentRead & Office.AppointmentCompose
): Promise<Date> {
  if (item.itemType === 'message') {
    const message = item as Office.MessageRead;
    return message.dateTimeCreated;
  } else if (item.itemType === 'appointment') {
    if (isAppointmentReadMode(item)) {
      const appointment = item as Office.AppointmentRead;
      return appointment.start;
    } else {
      const appointment = item as Office.AppointmentCompose;
      const date = await OutlookAsyncUtils.getAsyncTime(appointment.start);
      return date || new Date();
    }
  }
}

export async function getEndDateFromOutlookItem(
  item: Office.MessageRead & Office.AppointmentRead & Office.AppointmentCompose
): Promise<Date> {
  if (item.itemType === 'message') {
    return null;
  } else if (item.itemType === 'appointment') {
    if (isAppointmentReadMode(item)) {
      const appointment = item as Office.AppointmentRead;
      return appointment.end;
    } else {
      const appointment = item as Office.AppointmentCompose;
      const date = await OutlookAsyncUtils.getAsyncTime(appointment.end);
      return date || new Date();
    }
  }
}

export function isAppointmentReadMode(outlookItem: Office.AppointmentRead) {
  return outlookItem.itemType === 'appointment' && outlookItem.itemId;
}

/** Tries to retrieve item ID using a few different methods */
export async function getItemId(
  outlookItem: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
): Promise<string> {
  // if it's not a Compose mode, simply return the ID
  if (outlookItem.itemId) return outlookItem.itemId;

  // if it's Compose mode, itemId property is not available

  // first, try getting id with getAsyncId
  // which doesn't work if:
  //    0. API version < 1.8
  //    1. It's MacOS, in which case: getIdError instanceof AddInFeatureNotSupportedException
  //    2. id can't be retrieved because item is new and wasn't saved, in which case: getIdError.code === 9046
  //    3. It's Internet Explorer, in which case error message or description will be "Permission denied"
  // then, try getting id with saveAsync
  // which doesn't work if:
  //    1. It's MacOS, , in which case: getIdError instanceof AddInFeatureNotSupportedException
  //    2. Not sure if will work on IE
  // then, try the workaround with saving custom properties
  // which doesn't work if:
  //    1. custom properties are not saved yet, in which case: getItemIdBySearchOnCustomPropertiesError instanceof ItemIsNotSavedException
  // if nothing worked, combine all errors and rethrow

  try {
    return await OutlookAsyncUtils.getAsyncId(outlookItem);
  } catch (getIdError) {
    // if not supported, or any other error, try saveAsync
    try {
      return await OutlookAsyncUtils.saveAsync(outlookItem);
    } catch (saveError) {
      // if save function is not supported, try the workaround
      try {
        return await getItemIdBySearchOnCustomProperties(
          outlookItem,
          sharedProperties
        );
      } catch (getItemIdBySearchOnCustomPropertiesError) {
        // if item is not saved, UI should inform user
        if (
          getItemIdBySearchOnCustomPropertiesError instanceof
          ItemIsNotSavedException
        ) {
          throw getItemIdBySearchOnCustomPropertiesError;
        }
        // if problem is somewhere else, rethrow everything
        else {
          const errorsDescription = {
            getIdError: getIdError,
            saveError: saveError,
            getItemIdBySearchOnCustomPropertiesError:
              getItemIdBySearchOnCustomPropertiesError,
          };
          throw new ApplicationException(
            'Failed to get id for outlook item',
            errorsDescription
          );
        }
      }
    }
  }
}

/** This workaround is for MacOS, where save() function is not supported, and it's impossible to get item ID for items in Compose mode.
 * https://docs.microsoft.com/en-US/outlook/troubleshoot/calendars/cannot-save-meeting-as-draft-in-outlook-for-mac
 * Original workaround described in the article doesn't work: REST and Graph methods require back-end,
 * and EWS method throws errors when trying to sync items.
 * This method works differently: instead of using EWS syncing, it searches through all Appointments using a flag
 * saved in item's custom properties.
 * If flag wasn't yet set, then user must manually save the item.
 * @throws ItemIsNotSavedException
 */
async function getItemIdBySearchOnCustomProperties(
  outlookItem: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
): Promise<string> {
  assertHasGraphApi();

  const properties = await OutlookAsyncUtils.getAsyncCustomProperties(
    outlookItem
  );
  console.log(TAG, 'Retrieving item ID by search on custom properties');

  let searchFlagValue: string = properties.get('flag_for_mac_save_workaround');
  // if flag was set, search all Appointments by it's value
  if (searchFlagValue) {
    console.log(
      TAG,
      'Found flag_for_mac_save_workaround flag: ',
      searchFlagValue
    );

    const id = await GraphApi.getFirstEventIdByCustomProperty(
      searchFlagValue,
      sharedProperties
    );

    if (id) {
      console.log(TAG, 'Item Id found by the flag: ', id);
      return id;
    } else {
      // if flag is set, but not found in EWS, that means that flag wasn't yet saved to the server
      console.log(TAG, 'Item Id was not found by the flag');
      throw new ItemIsNotSavedException();
    }
  }
  // if flag wasn't set, then set the flag to any unique string
  // and ask user to save the item, so the flag could be read when this function runs again
  else {
    console.log(TAG, 'Flag was not set, setting the flag');
    searchFlagValue = new Date().valueOf().toString();
    properties.set('flag_for_mac_save_workaround', searchFlagValue);
    await OutlookAsyncUtils.saveAsyncCustomProperties(properties);
    throw new ItemIsNotSavedException();
  }
}

export function canSaveAttachments(): boolean {
  return (
    Office.context.requirements.isSetSupported('Mailbox', '1.8') ||
    CONFIG.isGraphApiEnabled
  );
}

async function getAttachmentsViaGraph(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined,
  includeInline: boolean
): Promise<Attachment[]> {
  const itemId = await getItemId(item, sharedProperties);

  return await getSupportedAttachmentsForItemId(
    itemId,
    item.itemType,
    sharedProperties,
    includeInline
  );
}

export async function getSupportedAttachmentsForItem(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined,
  includeInline = false
): Promise<SupportedAttachmentsResult> {
  const hostName = Office.context.mailbox.diagnostics.hostName;

  // Force Graph API fallback for iOS due to getAttachmentContentAsync issues
  // Also fallback if client doesn't support getAttachmentContentAsync (requirement set 1.8)
  if (
    hostName === 'OutlookIOS' ||
    !Office.context.requirements.isSetSupported('Mailbox', '1.8')
  ) {
    console.log(
      TAG,
      'Using Graph API fallback for attachments - Host:',
      hostName
    );
    const attachments = await getAttachmentsViaGraph(
      item,
      sharedProperties,
      includeInline
    );

    return { attachments, failedAttachmentNames: [] };
  }

  const result = await getDefaultSupportedAttachmentsForItem(item);

  // Nothing failed - nothing to recover.
  if (result.failedAttachmentNames.length === 0) {
    return result;
  }

  // Retry via Graph
  if (CONFIG.isGraphApiEnabled) {
    console.log(
      TAG,
      'Attachment content fetch failed; falling back to Graph API for',
      result.failedAttachmentNames
    );
    try {
      const graphAttachments = await getAttachmentsViaGraph(
        item,
        sharedProperties,
        includeInline
      );

      // Graph returns only file attachments, so keep any item attachments the JS API
      // already retrieved and report only failures Graph couldn't recover.
      const itemAttachmentsFromJsApi = result.attachments.filter(
        (a) => a.DataType === 'ItemAttachment'
      );
      const recoveredAttachments = [
        ...graphAttachments,
        ...itemAttachmentsFromJsApi,
      ];
      // Names aren't unique, so count occurrences: each recovered attachment can
      // clear at most one failure of the same name.
      const recoveredNameCounts = new Map<string, number>();
      for (const attachment of recoveredAttachments) {
        recoveredNameCounts.set(
          attachment.name,
          (recoveredNameCounts.get(attachment.name) ?? 0) + 1
        );
      }
      const stillFailedNames = result.failedAttachmentNames.filter((name) => {
        const remaining = recoveredNameCounts.get(name) ?? 0;
        if (remaining > 0) {
          recoveredNameCounts.set(name, remaining - 1);
          return false;
        }
        return true;
      });

      void ProductAnalytics.trackEvent({
        eventName: 'outlook_attachment_graph_fallback_succeeded',
        data: {
          failedAttachmentCount: result.failedAttachmentNames.length,
          stillFailedCount: stillFailedNames.length,
        },
      });

      return {
        attachments: recoveredAttachments,
        failedAttachmentNames: stillFailedNames,
      };
    } catch (graphError) {
      console.error(
        TAG,
        'Graph API attachment fallback also failed; saving without the failed attachments',
        graphError
      );
      void ProductAnalytics.trackEvent({
        eventName: 'outlook_attachment_graph_fallback_failed',
        data: { failedAttachmentCount: result.failedAttachmentNames.length },
      });
    }
  }

  // Graph unavailable or failed: keep what downloaded, report the rest.
  void ProductAnalytics.trackEvent({
    eventName: 'outlook_attachment_skipped',
    data: {
      failedAttachmentCount: result.failedAttachmentNames.length,
      isGraphApiEnabled: CONFIG.isGraphApiEnabled,
    },
  });

  return result;
}

export async function getDefaultSupportedAttachmentsForItem(
  item: SupportedOfficeItem
): Promise<SupportedAttachmentsResult> {
  const failedAttachmentNames: string[] = [];

  const attachments = await OutlookAsyncUtils.getAttachments(item);

  // If we have any attachment IDs that are empty or invalid, we're probably on Windows (New or Classic)
  // and this item is digitally signed or encrypted; so throw an error
  if (
    attachments.some(
      (attachment) => !attachment.id || attachment.id.startsWith('smime-')
    )
  ) {
    throw new OutlookAttachmentsNotAvailableException();
  }

  const attachmentsWithContent = await Promise.all(
    attachments
      .filter(
        (attachment) =>
          attachment.attachmentType ===
            Office.MailboxEnums.AttachmentType.File ||
          attachment.attachmentType === Office.MailboxEnums.AttachmentType.Item
      )
      .map(async (attachment) => {
        try {
          const content = await OutlookAsyncUtils.getAttachmentContent(
            item,
            attachment.id
          );

          return { attachment, content };
        } catch (error: unknown) {
          if (
            error instanceof OutlookException &&
            (error.originalException as Office.Error)?.name ===
              'AttachmentTypeNotSupported'
          ) {
            console.log(TAG, 'Skipping unsupported attachment', attachment);

            return undefined;
          }

          // The selected item changed mid-save, so the rest of the save would read the wrong item too.
          if (error instanceof OutlookWrongItemSelectedException) {
            throw error;
          }

          // Anything else is one attachment Outlook couldn't hand over — InvalidAttachmentId (9003),
          // an Exchange-side InternalServerError (9042), a missing attachment id. Exchange raises new
          // codes over time, so catching them one by one keeps losing whole saves to the next one.
          // Record it instead: the caller retries through Graph, and failing that saves the email
          // without it and tells the user which attachment was left out.
          console.warn(
            TAG,
            'Failed to get attachment content',
            attachment.name,
            error
          );
          failedAttachmentNames.push(attachment.name);

          return undefined;
        }
      })
  );

  const convertedAttachments = attachmentsWithContent
    .filter((pair) => pair?.content.content)
    .map((pair): Attachment => {
      const base64Content: string =
        pair.content.format ===
          Office.MailboxEnums.AttachmentContentFormat.Eml ||
        pair.content.format ===
          Office.MailboxEnums.AttachmentContentFormat.ICalendar
          ? btoa(pair.content.content)
          : pair.content.content;

      return {
        id: pair.attachment.id,
        name: (pair.attachment.name ?? '').replace(/\.(?=.*\.)/g, '_'), // replace all dots except the last one.
        // Compose-mode attachment details carry no content type; inline images are then identified
        // from their bytes when the body is processed.
        contentType:
          'contentType' in pair.attachment
            ? pair.attachment.contentType
            : undefined,
        contentBytes: base64Content,
        size: pair.attachment.size,
        attachmentType: 0,
        isInline: pair.attachment.isInline,
        contentId: pair.attachment['contentId'],
        DataType:
          pair.attachment.attachmentType ===
          Office.MailboxEnums.AttachmentType.File
            ? 'FileAttachment'
            : 'ItemAttachment',
      };
    });

  return { attachments: convertedAttachments, failedAttachmentNames };
}

export async function getSupportedAttachmentsForItemId(
  itemId: string,
  itemType: string,
  sharedProperties: Office.SharedProperties | undefined,
  includeInline = false
): Promise<Attachment[]> {
  try {
    const attachments = await GraphApi.getAttachments(
      itemId,
      itemType,
      sharedProperties
    );

    const convertedAttachments = attachments
      .filter(
        (attachment): attachment is MicrosoftGraph.FileAttachment =>
          !!(attachment as MicrosoftGraph.FileAttachment).contentBytes
      )
      .map(
        (attachment): Attachment => ({
          id: attachment.id,
          name: (attachment.name ?? '').replace(/\.(?=.*\.)/g, '_'), // replace all dots except the last one.
          contentType: attachment.contentType,
          contentBytes: attachment.contentBytes,
          size: attachment.size,
          attachmentType: 0,
          isInline: attachment.isInline,
          contentId: attachment.contentId,
          DataType:
            attachment['@odata.type'] === '#microsoft.graph.fileAttachment'
              ? 'FileAttachment'
              : attachment['@odata.type'],
        })
      );

    const supportedAttachments = includeInline
      ? convertedAttachments
      : convertedAttachments.filter((a) => !a.isInline);

    return supportedAttachments;
  } catch (error) {
    if (error.status === 404) {
      // this occurs when we were able get item id from the Exchange Server,
      // but the since user hasn't yet saved the draft item,
      // Exchange Server is still refusing to acknowledge existence of the item with this id
      throw new ItemIsNotSavedException();
    }
    throw error;
  }
}

/**
 * Outlook's own conversation id for the selected message, from Office.js.
 *
 * Deliberately not a Graph call: `item.conversationId` is available under the base Office.js
 * permissions and so works in the restricted add-in variant too, which has no Graph access at all.
 * That makes it the only conversation signal available to every user.
 *
 * Mailbox-scoped, which is why it ranks below the RFC chain server-side - two Darzin users logging
 * the same exchange from their own inboxes get different values, and only the RFC chain converges
 * them.
 */
export function getConversationIdFromOutlookItem(
  item: SupportedOfficeItem
): string | null {
  try {
    // Undefined for appointments, and for a compose-mode message the user has not saved yet.
    if (!item.conversationId) {
      return null;
    }

    // Stored in REST format, the form Graph returns, so ids from manual save, add-in autosave and
    // the server-side Autosync all match on equality. Office.js uses the EWS form on desktop and web.
    return getItemRestId(item.conversationId);
  } catch {
    return null;
  }
}

/**
 * This message's Message-ID and References chain, via Graph.
 *
 * Requires Graph, so it returns nothing in the restricted variant. Callers should still capture
 * {@link getConversationIdFromOutlookItem}, which works everywhere.
 */
export async function getMessageReferenceIds(
  itemId: string,
  sharedProperties: Office.SharedProperties | undefined
): Promise<MessageReferenceIds> {
  return await GraphApi.getMessageReferenceIds(itemId, sharedProperties);
}

export async function getAppointmentUids(
  itemId: string,
  sharedProperties: Office.SharedProperties | undefined
): Promise<AppointmentUids> {
  return await GraphApi.getAppointmentUids(itemId, sharedProperties);
}

export async function isReadOnlySharedItem(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties | undefined
): Promise<boolean> {
  if (!sharedProperties) {
    return false;
  }

  if (
    (sharedProperties.delegatePermissions &
      Office.MailboxEnums.DelegatePermissions.EditOwn) ===
      0 &&
    (sharedProperties.delegatePermissions &
      Office.MailboxEnums.DelegatePermissions.EditAll) ===
      0
  ) {
    return true;
  }

  try {
    console.log(TAG, 'Checking if shared item is read-only');

    const propertyName = 'read_only_permission_test';
    const properties = await OutlookAsyncUtils.getAsyncCustomProperties(item);

    properties.set(propertyName, '1');
    await OutlookAsyncUtils.saveAsyncCustomProperties(properties);

    properties.remove(propertyName);
    await OutlookAsyncUtils.saveAsyncCustomProperties(properties);

    console.log(
      TAG,
      'Checking if shared item is read-only: ',
      'Able to save custom properties, not read-only'
    );

    return false;
  } catch (error: unknown) {
    console.log(
      TAG,
      'Checking if shared item is read-only: ',
      'Received error saving custom properties, assuming read-only',
      error
    );

    return true;
  }
}

export async function isSavedMeeting(
  item: SupportedOfficeItem,
  sharedProperties: Office.SharedProperties
) {
  if (!CONFIG.isGraphApiEnabled) {
    return false;
  }

  if (item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
    return false;
  }

  const itemId = await getItemId(item, sharedProperties);

  const [uids, startDate] = await Promise.all([
    getAppointmentUids(itemId, sharedProperties),
    getDateFromOutlookItem(item),
  ]);

  const response = await findDuplicateMeeting({
    MeetingUID: uids.uid,
    MeetingRecurrenceID: uids.recurrenceId,
    StartDate: toIsoWithTimeZone(startDate),
  });

  return response.IsMatchFound;
}

export function getItemRestId(itemId: string) {
  const hostName = Office.context.mailbox.diagnostics.hostName;
  if (hostName === 'OutlookIOS' || hostName === 'OutlookAndroid') {
    // itemId is already REST-formatted.
    return itemId;
  } else {
    // Convert to an item ID for API v2.0.
    return Office.context.mailbox.convertToRestId(
      itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );
  }
}
